/**
 * Simple HTML Template Engine
 *
 * Supports:
 * - Variable interpolation: {{variable}}
 * - Conditional blocks: {{#if condition}}...{{/if}}
 * - Else blocks: {{#if condition}}...{{else}}...{{/if}}
 *
 * Future enhancements:
 * - {{#each}} loops
 * - {{#unless}} negative conditionals
 * - Nested conditionals
 * - Helper functions
 */

export interface TemplateData {
  [key: string]: string | boolean | number | null | undefined
}

export class TemplateEngine {
  /**
   * Render template with data
   */
  static render(template: string, data: TemplateData): string {
    let html = template

    // Process conditional blocks first ({{#if}}...{{/if}})
    html = this.processConditionals(html, data)

    // Then process variable interpolation ({{variable}})
    html = this.processVariables(html, data)

    return html
  }

  /**
   * Process conditional blocks: {{#if condition}}...{{else}}...{{/if}}
   */
  private static processConditionals(html: string, data: TemplateData): string {
    // Match {{#if variable}}...{{else}}...{{/if}} or {{#if variable}}...{{/if}}
    const conditionalRegex = /\{\{#if\s+(\w+)\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g

    return html.replace(conditionalRegex, (match, variable, trueBlock, falseBlock) => {
      const value = data[variable]
      const condition = this.isTruthy(value)

      if (condition) {
        return trueBlock || ''
      } else {
        return falseBlock || ''
      }
    })
  }

  /**
   * Process variable interpolation: {{variable}}
   */
  private static processVariables(html: string, data: TemplateData): string {
    // Match {{variable}} but not {{#if}} or {{/if}} or {{else}}
    const variableRegex = /\{\{(?!#|\/|else)(\w+)\}\}/g

    return html.replace(variableRegex, (match, variable) => {
      const value = data[variable]
      return value !== null && value !== undefined ? String(value) : ''
    })
  }

  /**
   * Check if value is truthy (for conditionals)
   */
  private static isTruthy(value: unknown): boolean {
    if (value === null || value === undefined) return false
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    if (typeof value === 'string') return value.length > 0
    return true
  }
}

/**
 * Template helper to escape HTML entities
 */
// export function escapeHtml(text: string): string {
//   const map: Record<string, string> = {
//     '&': '&amp;',
//     '<': '&lt;',
//     '>': '&gt;',
//     '"': '&quot;',
//     "'": '&#039;'
//   }
//   return text.replace(/[&<>"']/g, (m) => map[m])
// }

/**
 * Template helper to sanitize HTML attributes
 */
// export function sanitizeAttribute(value: string): string {
//   // Remove any script-like content
//   return value.replace(/javascript:/gi, '').replace(/on\w+\s*=/gi, '')
// }
